import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  TextField,
  IconButton,
  Tooltip,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Collapse,
  Button,
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  ThumbDownOutlined as ThumbDownOutlinedIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ArrowBack as BackIcon,
  MenuBook as KBIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { KBChatMessage } from '../types';
import { streamChat, submitFeedback } from '../services/protocolKbChat';
import { v4 as uuidv4 } from 'uuid';

function parseThinking(content: string): { thoughts: string[]; answer: string } {
  const thoughts: string[] = [];
  let answer = '';
  let remaining = content;

  while (remaining.length > 0) {
    const startIdx = remaining.indexOf('<think>');
    if (startIdx === -1) {
      answer += remaining;
      break;
    }

    answer += remaining.slice(0, startIdx);
    remaining = remaining.slice(startIdx + 7);

    const endIdx = remaining.indexOf('</think>');
    if (endIdx === -1) {
      thoughts.push(remaining);
      break;
    }

    thoughts.push(remaining.slice(0, endIdx));
    remaining = remaining.slice(endIdx + 8);
  }

  return { thoughts: thoughts.filter(t => t.trim()), answer: answer.trim() };
}

const EXAMPLE_QUESTIONS = [
  'How do I rename a visit?',
  'How do I add a new form to a visit?',
];

const KBAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<KBChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState(() => {
    const storageKey = 'protocol-kb-chat-session-id';
    const stored = localStorage.getItem(storageKey);
    if (stored) return stored;
    const newId = uuidv4();
    localStorage.setItem(storageKey, newId);
    return newId;
  });
  const [expandedThoughts, setExpandedThoughts] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const sendMessage = async (prompt: string) => {
    if (!prompt || isStreaming) return;

    setInput('');
    setError(null);

    const userMsg: KBChatMessage = {
      id: uuidv4(),
      role: 'user',
      content: prompt,
      timestamp: new Date(),
    };
    const assistantMsg: KBChatMessage = {
      id: uuidv4(),
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setIsStreaming(true);

    await streamChat({
      prompt,
      sessionId,
      onChunk: (text) => {
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last.role === 'assistant') {
            updated[updated.length - 1] = { ...last, content: last.content + text };
          }
          return updated;
        });
      },
      onDone: () => setIsStreaming(false),
      onError: (err) => {
        setError(err.message);
        setIsStreaming(false);
      },
    });
  };

  const handleSend = () => sendMessage(input.trim());

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setMessages([]);
    setError(null);
    const newId = uuidv4();
    localStorage.setItem('protocol-kb-chat-session-id', newId);
    setSessionId(newId);
    setExpandedThoughts(new Set());
  };

  const toggleThoughts = (msgId: string) => {
    setExpandedThoughts(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) next.delete(msgId);
      else next.add(msgId);
      return next;
    });
  };

  const handleFeedback = (msgId: string, rating: number) => {
    setMessages(prev =>
      prev.map(m => {
        if (m.id !== msgId) return m;
        const toggled = m.feedbackRating === rating ? undefined : rating;
        return { ...m, feedbackRating: toggled, feedbackComment: undefined, feedbackSubmitted: false };
      })
    );

    const msgIndex = messages.findIndex(m => m.id === msgId);
    const assistantMsg = messages[msgIndex];
    const questionMsg = msgIndex > 0 ? messages[msgIndex - 1] : null;

    if (assistantMsg && questionMsg && rating === 1) {
      submitFeedback({
        session_id: sessionId,
        question: questionMsg.content,
        answer: assistantMsg.content,
        rating,
      }).catch(() => {});
    }
  };

  const handleSubmitDislike = (msgId: string) => {
    const msgIndex = messages.findIndex(m => m.id === msgId);
    const assistantMsg = messages[msgIndex];
    const questionMsg = msgIndex > 0 ? messages[msgIndex - 1] : null;

    if (assistantMsg && questionMsg) {
      submitFeedback({
        session_id: sessionId,
        question: questionMsg.content,
        answer: assistantMsg.content,
        rating: -1,
        comment: assistantMsg.feedbackComment,
      }).catch(() => {});
      setMessages(prev =>
        prev.map(m => (m.id === msgId ? { ...m, feedbackSubmitted: true } : m))
      );
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 0, px: 3, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 130px)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
        <Tooltip title="Back to Trials">
          <IconButton onClick={() => navigate('/')} size="small" sx={{ mt: 0.25, color: 'text.primary' }}>
            <BackIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Typography variant="h6" component="h1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            Knowledge Base
          </Typography>
          <Chip
            label="Beta Version"
            size="medium"
            sx={{
              mt: 0.5,
              fontWeight: 600,
              fontSize: '0.85rem',
              bgcolor: 'warning.main',
              color: 'warning.contrastText',
            }}
          />
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      )}

      {/* Messages */}
      <Box ref={scrollRef} sx={{
        flex: 1, overflow: 'auto',
        scrollbarGutter: 'stable',
        '&::-webkit-scrollbar': { width: 6 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
        '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'grey.400' },
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0,0,0,0.15) transparent',
      }}>
          <Box sx={{ maxWidth: 820, mx: 'auto', px: 3, pt: 3, pb: 20 }}>
            {messages.length === 0 ? (
              <Box sx={{
                py: 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}>
                <KBIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                  Knowledge Base Assistant
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, whiteSpace: 'nowrap' }}
                >
                  Ask questions about configuration, features, and how-to guides.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  {EXAMPLE_QUESTIONS.map((q) => (
                    <Typography
                      key={q}
                      variant="body2"
                      onClick={() => sendMessage(q)}
                      sx={{
                        color: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {q}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ) : (
              messages.map((msg) => (
                <Box key={msg.id} sx={{ mb: 3 }}>
                  {msg.role === 'user' ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Box sx={{
                        bgcolor: 'primary.main',
                        color: 'common.white',
                        px: 2.5,
                        py: 1.5,
                        borderRadius: '18px 18px 4px 18px',
                        maxWidth: '70%',
                      }}>
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{msg.content}</Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ pt: 2 }}>
                      {(() => {
                        const { thoughts, answer } = parseThinking(msg.content);
                        const hasContent = answer || thoughts.length > 0;
                        const isLastAssistant = msg.id === messages[messages.length - 1]?.id;
                        const currentlyStreaming = isStreaming && isLastAssistant;
                        const thoughtsOpen = expandedThoughts.has(msg.id);

                        if (!hasContent && currentlyStreaming) {
                          return (
                            <Box sx={{
                              display: 'flex', alignItems: 'center', gap: 1,
                              '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                              animation: 'pulse 1.5s ease-in-out infinite',
                            }}>
                              <CircularProgress size={14} />
                              <Typography variant="body2" color="text.secondary">Thinking...</Typography>
                            </Box>
                          );
                        }

                        if (!hasContent) return null;

                        return (
                          <>
                            {thoughts.length > 0 && (
                              <Box sx={{ mb: 1.5 }}>
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={thoughtsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  endIcon={currentlyStreaming && !answer ? <CircularProgress size={10} /> : undefined}
                                  onClick={() => toggleThoughts(msg.id)}
                                  sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500, fontSize: '0.75rem' }}
                                >
                                  Thought process ({thoughts.length} {thoughts.length === 1 ? 'step' : 'steps'})
                                </Button>
                                <Collapse in={thoughtsOpen}>
                                  <Box sx={{
                                    mt: 0.75, ml: 1, pl: 1.5,
                                    borderLeft: '2px solid', borderColor: 'divider',
                                  }}>
                                    {thoughts.map((t, i) => (
                                      <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.8rem', lineHeight: 1.5 }}>
                                        {t.trim()}
                                      </Typography>
                                    ))}
                                  </Box>
                                </Collapse>
                              </Box>
                            )}

                            {answer ? (
                              <>
                                <Box sx={{
                                  overflow: 'hidden',
                                  '& p': { mt: 0, mb: 1.5, lineHeight: 1.7 },
                                  '& h1': { fontSize: '1.3rem', mt: 2.5, mb: 1, fontWeight: 600 },
                                  '& h2': { fontSize: '1.15rem', mt: 2, mb: 1, fontWeight: 600 },
                                  '& h3': { fontSize: '1.05rem', mt: 1.5, mb: 0.75, fontWeight: 600 },
                                  '& h4': { fontSize: '1rem', mt: 1.5, mb: 0.75, fontWeight: 600 },
                                  '& ul,& ol': { pl: 3, mb: 1.5 },
                                  '& li': { mb: 0.5 },
                                  '& pre': { bgcolor: 'action.hover', color: 'text.primary', p: 2, borderRadius: 1, overflow: 'auto', fontSize: '0.85rem', my: 1.5, maxWidth: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word', border: '1px solid', borderColor: 'divider' },
                                  '& code': { bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5, fontSize: '0.85rem', fontFamily: 'monospace' },
                                  '& pre code': { bgcolor: 'transparent', color: 'inherit', p: 0 },
                                  '& table': { width: '100%', borderCollapse: 'collapse', my: 1.5, fontSize: '0.875rem' },
                                  '& th,& td': { border: '1px solid', borderColor: 'divider', px: 1.5, py: 1, textAlign: 'left' },
                                  '& th': { bgcolor: 'action.hover', fontWeight: 600 },
                                  '& blockquote': { borderLeft: '3px solid', borderColor: 'divider', pl: 2, ml: 0, my: 1.5, color: 'text.secondary' },
                                }}>
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{answer}</ReactMarkdown>
                                </Box>
                                {!isStreaming && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <IconButton size="small" onClick={() => handleFeedback(msg.id, 1)}
                                        color={msg.feedbackRating === 1 ? 'primary' : 'default'}>
                                        {msg.feedbackRating === 1 ? <ThumbUpIcon sx={{ fontSize: 15 }} /> : <ThumbUpOutlinedIcon sx={{ fontSize: 15 }} />}
                                      </IconButton>
                                      <IconButton size="small" onClick={() => handleFeedback(msg.id, -1)}
                                        color={msg.feedbackRating === -1 ? 'error' : 'default'}>
                                        {msg.feedbackRating === -1 ? <ThumbDownIcon sx={{ fontSize: 15 }} /> : <ThumbDownOutlinedIcon sx={{ fontSize: 15 }} />}
                                      </IconButton>
                                      {msg.feedbackSubmitted && (
                                        <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                                          Feedback sent
                                        </Typography>
                                      )}
                                    </Box>
                                    {msg.feedbackRating === -1 && !msg.feedbackSubmitted && (
                                      <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-end' }}>
                                        <TextField
                                          size="small"
                                          multiline
                                          minRows={1}
                                          maxRows={5}
                                          placeholder="What was wrong with this answer?"
                                          value={msg.feedbackComment || ''}
                                          onChange={(e) => setMessages(prev =>
                                            prev.map(m => (m.id === msg.id ? { ...m, feedbackComment: e.target.value } : m))
                                          )}
                                          sx={{ flex: 1 }}
                                        />
                                        <IconButton size="small" color="primary" onClick={() => handleSubmitDislike(msg.id)}
                                          disabled={!msg.feedbackComment?.trim()}>
                                          <SendIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </>
                            ) : currentlyStreaming ? (
                              <Box sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                                animation: 'pulse 1.5s ease-in-out infinite',
                              }}>
                                <CircularProgress size={14} />
                                <Typography variant="body2" color="text.secondary">Preparing answer...</Typography>
                              </Box>
                            ) : null}
                          </>
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              ))
            )}
          </Box>
        </Box>

      {/* Input area */}
      <Box sx={{ scrollbarGutter: 'stable', pb: 3, pt: 1.5 }}>
        <Box sx={{ maxWidth: 820, mx: 'auto', px: 3 }}>
          <Paper
            variant="outlined"
            sx={{
              width: '100%',
              borderColor: 'divider',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 0.5,
            }}
          >
            <Tooltip title="New conversation">
              <span>
                <IconButton
                  onClick={handleClear}
                  disabled={isStreaming || messages.length === 0}
                  size="small"
                  sx={{ color: 'primary.main', mr: 0.5 }}
                >
                  <RefreshIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <TextField
              fullWidth
              placeholder="Ask a question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
              variant="standard"
              InputProps={{ disableUnderline: true }}
            />
            <Tooltip title="Send">
              <span>
                <IconButton
                  onClick={handleSend}
                  disabled={!input.trim() || isStreaming}
                  size="small"
                  sx={{
                    color: !input.trim() || isStreaming ? 'text.disabled' : 'primary.main',
                    '&:hover': { color: 'primary.dark' },
                    '&.Mui-disabled': { color: 'text.disabled' },
                    width: 32,
                    height: 32,
                    ml: 1,
                  }}
                >
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
};

export default KBAdminPage;
